import * as React from 'react';
import { Modal, ModalBody, ModalFooter, ModalSize, CustomButton, ButtonType, AlertMessage, AlertType, CustomSubmitButton, CustomSubmitButtonType, TextInput, Form, } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { RiskPotReportPreAssessmentApi } from '../../../api/RiskPotReportPreAssessmentApi';
import { setMessage } from '../../../helpers/MessageHelper';
import { IRiskPotAssessment, IRiskPotTreatmentAssessment } from '../../../models/IRiskPotReport';
import { StandardPhraseSelection } from '../../StandardPhraseSelection/StandardPhraseSelection';
import { IStandardPhrase } from '../../../models/IStandardPhrase';
import { formatCurrency } from '../../../helpers/CurrencyHelper';

interface ISubmitAssessmentModal {
    changeSuccessMessage: (message: string) => void;
    getRiskPotReport: () => void;
    isCommentRequired: boolean;
    setIsCommentRequired: React.Dispatch<React.SetStateAction<boolean>>;
    riskPotExpandAmount: number;
    isApprovedWithReservation?: boolean;
    riskPotReportId: number;
    assessedTreatments: IRiskPotTreatmentAssessment[];
    activePreAssessmentDecisionComment?: string;
}

export const SubmitAssessmentModal: React.FunctionComponent<ISubmitAssessmentModal> = ({ changeSuccessMessage, isCommentRequired, getRiskPotReport, setIsCommentRequired, riskPotExpandAmount, isApprovedWithReservation, riskPotReportId, assessedTreatments, activePreAssessmentDecisionComment }) => {
    const auth = useAuth();
    const riskPotReportPreAssessmentApi = new RiskPotReportPreAssessmentApi(auth);

    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [successMessage, setSuccessMessage] = React.useState<string>("");
    const [standardPhrases, setStandardPhrases] = React.useState<IStandardPhrase[]>();
    const [showStandardPhraseTable, setShowStandardPhraseTable] = React.useState<boolean>(true);

    React.useEffect(() => {
        getStandardPhrases()
    }, [isApprovedWithReservation])

    const getStandardPhrases = () => {
            riskPotReportPreAssessmentApi.getStandardPhrases((data) => {
                setStandardPhrases(data);
            });
    }

    const saveAssessment = (data: IRiskPotAssessment) => {
        setIsSubmitting(true);        
        data.riskPotReportId = riskPotReportId;
        
        riskPotReportPreAssessmentApi.savePreAssessmentDecisionComment(data, () => {
            document.getElementById("close-submit-assessment-modal")?.click();
            changeSuccessMessage("Beslutsmotivering för förhandsbedömingen har sparats");
            getRiskPotReport();
        }, (error) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        }, () => {
            setIsSubmitting(false);
        });
    }

    const submitAssessment = (data: IRiskPotAssessment) => {
        setIsSubmitting(true);        
        data.treatments = assessedTreatments;
        data.riskPotReportId = riskPotReportId;
        
        riskPotReportPreAssessmentApi.closePreAssessment(data, () => {
            document.getElementById("close-submit-assessment-modal")?.click();
            changeSuccessMessage("Beslutet för förhandsbedömingen har skickats");
            getRiskPotReport();
        }, (error) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        }, () => {
            setIsSubmitting(false);
        });
    }

    const changeErrorMessage = (message: string) => {
        var newMessage = setMessage(message, errorMessage)
        setErrorMessage(newMessage);
    }

    const changeSuccessModalMessage = (message: string) => {
        var newMessage = setMessage(message, successMessage)
        setSuccessMessage(newMessage);
    }

    return (
        <Modal id="submit-assessment-modal" header="Hantera beslutsmotivering" modalSize={ModalSize.extraLarge}>
            <ModalBody>
                <AlertMessage duration={3000} alertType={AlertType.danger}>{errorMessage}</AlertMessage>
                <AlertMessage duration={3000} alertType={AlertType.success}>{successMessage}</AlertMessage>
                {
                    <StandardPhraseSelection
                        standardPhrases={standardPhrases}
                        changeSuccessMessage={changeSuccessModalMessage}
                        changeErrorMessage={changeErrorMessage}
                        standardPhraseFeature="riskPotReportPreAssessmentDecisionStandardPhrase"
                        getStandardPhrases={getStandardPhrases}
                        saveForm={saveAssessment}
                        submitForm={submitAssessment}
                        isSubmittingForm={isSubmitting}
                        showStandardPhraseTable={showStandardPhraseTable}
                        setShowStandardPhraseTable={setShowStandardPhraseTable}
                        defaultValue={activePreAssessmentDecisionComment}
                        isRequired={isCommentRequired}
                    >
                        {
                            riskPotExpandAmount == 0
                                ? <p>Totalbeloppet för riskpotten kommer inte att utökas</p>
                                : <TextInput name="readonlyExpandAmount" label="Godkänt belopp" defaultValue={`${formatCurrency(riskPotExpandAmount)}`} readonly inlineLabel />
                        }
                        {
                            isCommentRequired === true
                                ? <p>Beslut om ej godkända åtgärdskoder, en eller flera, kräver en motivering</p>
                                : <p>Beslut om godkända åtgärdskoder, alla, kräver inte motivering men det är valfritt att lämna en motivering</p>
                        }
                    </StandardPhraseSelection>
                }
            </ModalBody>
            <ModalFooter>
                <span className={`${showStandardPhraseTable ? "" : "d-none"}`}>
                    <CustomButton buttonType={ButtonType.general} buttonText="Avbryt" data-dismiss="modal" id="close-submit-assessment-modal" onClick={() => setIsCommentRequired(false)} />
                </span>
            </ModalFooter>
        </Modal >
    )
}